/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2024 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import styled, { css, SimpleInterpolation } from "styled-components";
import { devices, useScreenSize } from "components/shared/Styles";
import * as React from "react";
import { CmsImageAttributes, CmsImageFormat } from "api/CmsApi";
import {
  GuidePageParagraph,
  imageWidth,
  TEXT_COLUMN_PADDING_FALLBACK,
  TEXT_COLUMN_WIDTH,
  textWidth,
  GuidePageCaption,
  IMAGE_COLUMN_WIDTH,
  IMAGE_COLUMN_PADDING_FALLBACK,
} from "./GuidePageSimpleBlocks";
import { useEffect, useRef } from "react";

export const GuidePageImageBlock: React.FC<{
  image: CmsImageAttributes;
  highFetchPriority?: boolean;
  className?: string;
}> = ({ image, className, highFetchPriority = false }) => {
  const screenWidth = useScreenSize();

  const smallImage =
    image.width <=
    Math.min(TEXT_COLUMN_WIDTH, screenWidth - TEXT_COLUMN_PADDING_FALLBACK);

  const ref = useRef<HTMLImageElement>(null);

  useEffect(() => {
    if (ref.current && highFetchPriority) {
      ref.current.setAttribute("fetchpriority", "high");
    }
  }, [highFetchPriority]);

  const srcSet = [...Object.values(image.formats), image]
    .map((format: CmsImageFormat): string => {
      return `${format.url} ${format.width}w`;
    })
    .join(", ");

  return (
    <GuidePageImageContainer className={className} smallImage={smallImage}>
      {smallImage ? (
        <GuidePageParagraph>
          <GuidePageSmallImage
            src={image.url}
            srcSet={srcSet}
            aspectRatio={image.width / image.height}
            alt={image.alternativeText ?? image.caption ?? image.name}
            width={image.width}
            height={image.height}
            ref={ref}
          />
        </GuidePageParagraph>
      ) : (
        <GuidePageImage
          src={image.url}
          srcSet={srcSet}
          sizes={`(max-width: ${
            IMAGE_COLUMN_WIDTH + IMAGE_COLUMN_PADDING_FALLBACK
          }px) calc(100vw - ${IMAGE_COLUMN_PADDING_FALLBACK}px), ${IMAGE_COLUMN_WIDTH}px`}
          aspectRatio={image.width / image.height}
          alt={image.alternativeText ?? image.caption ?? image.name}
          ref={ref}
        />
      )}

      {image.caption && <GuidePageCaption>{image.caption}</GuidePageCaption>}
    </GuidePageImageContainer>
  );
};

const GuidePageImageContainer = styled.div<{ smallImage: boolean }>`
  margin-bottom: ${({ smallImage }): number => (smallImage ? 32 : 64)}px;
  margin-top: ${({ smallImage }): number => (smallImage ? 32 : 64)}px;

  & > * {
    margin-left: auto;
    margin-right: auto;
  }

  ${({ smallImage }): SimpleInterpolation => {
    if (smallImage) {
      return css`
        ${devices.small} {
          margin-bottom: 48px;
          margin-top: 48px;
        }
      `;
    }
  }}
`;

const imageBoxShadow = css`
  box-shadow: 0 3px 4px 0 #00023808, 0 0 2px 0 #00023826,
    0 12px 24px 0 #393fc717;
`;

const GuidePageImage = styled.img<{ aspectRatio: number }>`
  ${imageWidth};
  ${imageBoxShadow};
  border-radius: 24px;
  display: block;
  aspect-ratio: ${({ aspectRatio }): number => aspectRatio};
  height: auto;
`;

const GuidePageSmallImage = styled.img<{ aspectRatio: number }>`
  ${imageBoxShadow};
  border-radius: 24px;
  display: block;
  aspect-ratio: ${({ aspectRatio }): number => aspectRatio};
`;
